import React from 'react';
import { Stack, TextField, PrimaryButton, Spinner, Text, Image, MessageBar, MessageBarType, getTheme, Checkbox } from '@fluentui/react';
import { BookContent } from '../type';
import { useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import IvicosStrings from 'kits/language/stringKit';

interface BookViewProps {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    isSearching: boolean;
    searchResults: BookContent[];
    selectBook: (book: BookContent) => void;
    selectedBook?: BookContent;
    searchBooks: () => void;
    clearSearchResults: () => void;
    error: string;
    setError: (error: string) => void;
    preserveAspectRatio: boolean;
    setPreserveAspectRatio: (preserve: boolean) => void;
}

export const BookView: React.FC<BookViewProps> = ({
    searchTerm,
    setSearchTerm,
    isSearching,
    searchResults,
    selectBook,
    selectedBook,
    searchBooks,
    clearSearchResults,
    error,
    setError,
    preserveAspectRatio,
    setPreserveAspectRatio
}) => {
    const theme = getTheme();
    const [editedWidget] = useEditedWidget();
    const isEditMode = editedWidget?.content && editedWidget.content.author && editedWidget.content.title;

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            {!isEditMode && (
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                    <TextField
                        label={IvicosStrings.personalMeetingCornerStrings.book.searchForBook}
                        placeholder={IvicosStrings.personalMeetingCornerStrings.book.searchPlaceholder}
                        value={searchTerm}
                        onChange={(_, v) => {
                            const newValue = v || '';
                            setSearchTerm(newValue);
                            if (!newValue.trim()) {
                                clearSearchResults();
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && searchTerm.trim()) {
                                searchBooks();
                            }
                        }}
                        styles={{ root: { minWidth: 250, width: '100%' } }}
                    />
                    <PrimaryButton
                        text={isSearching ? IvicosStrings.personalMeetingCornerStrings.book.searching : IvicosStrings.personalMeetingCornerStrings.book.search}
                        onClick={() => {
                            if (!searchTerm.trim()) {
                                setError(IvicosStrings.personalMeetingCornerStrings.book.pleaseEnterSearchTerm);
                                return;
                            }
                            setError('');
                            searchBooks();
                        }}
                        disabled={isSearching}
                    />
                </Stack>
            )}

            {error && (
                <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')}>
                    {error}
                </MessageBar>
            )}
            {isSearching && <Spinner label={IvicosStrings.personalMeetingCornerStrings.book.searchingBooks} />}
            {!isSearching && searchResults.length > 0 && (
                <Stack tokens={{ childrenGap: 8 }} styles={{ root: { maxHeight: 250, overflowY: 'auto', margin: '20px auto' } }}>
                    {searchResults.map((book, idx) => (
                        <Stack
                            key={idx}
                            horizontal
                            tokens={{ childrenGap: 12 }}
                            verticalAlign="center"
                            styles={{
                                root: {
                                    cursor: 'pointer',
                                    borderRadius: 6,
                                    padding: 8,
                                    background: theme.palette.white,
                                    border:
                                        selectedBook?.title === book.title
                                            ? `2px solid ${theme.palette.themePrimary}`
                                            : `1px solid ${theme.palette.neutralLight}`
                                }
                            }}
                            onClick={() => selectBook(book)}
                        >
                            {book.coverUrl && <Image src={book.coverUrl} width={40} height={60} styles={{ root: { objectFit: 'cover', borderRadius: 4 } }} />}
                            <Stack>
                                <Text variant="mediumPlus">{book.title}</Text>
                                <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                    {book.author}
                                </Text>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            )}
            {(selectedBook || isEditMode) && (
                <Stack
                    tokens={{ childrenGap: 8 }}
                    horizontal
                    horizontalAlign="start"
                    styles={{
                        root: {
                            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
                            borderRadius: 8,
                            padding: 16,
                            background: theme.palette.neutralLighter
                        }
                    }}
                >
                    {(selectedBook?.coverUrl || editedWidget?.content?.coverUrl) && (
                        <Image
                            src={selectedBook?.coverUrl || editedWidget?.content?.coverUrl}
                            width={100}
                            height={150}
                            styles={{ root: { objectFit: 'cover', borderRadius: 6 } }}
                        />
                    )}
                    <Stack>
                        <Text variant="large">{selectedBook?.title || editedWidget?.content?.title}</Text>
                        <Text variant="medium" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                            {IvicosStrings.personalMeetingCornerStrings.book.author.replace(
                                '{authorName}',
                                selectedBook?.author || editedWidget?.content?.author
                            )}
                        </Text>
                        {(selectedBook?.publishedDate || editedWidget?.content?.publishedDate) && (
                            <Text variant="medium" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                {IvicosStrings.personalMeetingCornerStrings.book.published.replace(
                                    '{publishedDate}',
                                    selectedBook?.publishedDate || editedWidget?.content?.publishedDate
                                )}
                            </Text>
                        )}
                        {(selectedBook?.isbn || editedWidget?.content?.isbn) && (
                            <Text variant="medium" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                {IvicosStrings.personalMeetingCornerStrings.book.isbn.replace(
                                    '{isbnNumber}',
                                    selectedBook?.isbn || editedWidget?.content?.isbn
                                )}
                            </Text>
                        )}
                        {(selectedBook?.infoUrl || editedWidget?.content?.infoUrl) && (
                            <a href={selectedBook?.infoUrl || editedWidget?.content?.infoUrl} target="_blank" rel="noopener noreferrer">
                                {IvicosStrings.personalMeetingCornerStrings.book.openLibrary}
                            </a>
                        )}
                    </Stack>
                </Stack>
            )}

            <Checkbox
                label={IvicosStrings.personalMeetingCornerStrings.createWidget.preserveAspectRatio}
                checked={preserveAspectRatio}
                onChange={(_, checked) => setPreserveAspectRatio(checked || false)}
            />
        </Stack>
    );
};
